import HighlightOffOutlined from '@mui/icons-material/HighlightOffOutlined';
import { outlinedInputClasses } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { t } from 'i18next';
import { useFormContext } from 'react-hook-form';

import { CheckBoxFormField } from '~components/FormFields/CheckBoxFormField';
import { SingleDateTimeFormField } from '~components/FormFields/SingleDateTimeFormField';
import { TextFormField } from '~components/FormFields/TextFormField';
import { PseudoLink } from '~components/Helpers/PseudoLink';

export default function DateAndTimeV2() {
  const form = useFormContext();

  const watchedJobStartSameAsOrderStart = form.watch('jobStartSameAsOrderStart');

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: watchedJobStartSameAsOrderStart
          ? '260px auto auto'
          : '260px 260px 24px',
        gap: 0,
      }}
    >
      <Box sx={{ pr: 0.5 }}>
        <SingleDateTimeFormField
          control={form.control}
          dataUi="page-sales-orders__order-form-v2__date-and-time__order-start-date-input"
          dateLabel={t('order.form.start_date')}
          dense
          errors={form.formState.errors}
          isRequired
          name="loadAt"
          timeLabel={t('order.form.start_time')}
        />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pl: 0.5 }}>
        <Box sx={{ display: watchedJobStartSameAsOrderStart ? 'none' : 'block' }}>
          <SingleDateTimeFormField
            control={form.control}
            dataUi="page-sales-orders__order-form-v2__date-and-time__job-start-date-input"
            dateLabel={t('order.form.job_start_date')}
            dense
            disabled={watchedJobStartSameAsOrderStart}
            errors={form.formState.errors}
            isRequired
            name="jobStartAt"
            timeLabel={t('order.form.job_start_time')}
          />
        </Box>

        <Box sx={{ display: 'flex' }}>
          <TextFormField
            control={form.control}
            errors={form.formState.errors}
            label={t('order.form_v2.truck_stagger')}
            name="truckDelay"
            type="number"
            sx={{
              // setting a max width to prevent the labels from text-wrapping
              // it would be better to use no-wrap but that can cause this component to exceed the width of the parent card
              maxWidth: '85px',
              [`& .${outlinedInputClasses.notchedOutline}`]: {
                borderBottomRightRadius: 0,
                borderTopRightRadius: 0,
              },
            }}
          />

          <TextFormField
            control={form.control}
            errors={form.formState.errors}
            label={t('order.form_v2.per_stagger')}
            name="trucksPerDelay"
            type="number"
            sx={{
              ml: '-1px',
              [`& .${outlinedInputClasses.notchedOutline}`]: {
                borderBottomLeftRadius: 0,
                borderLeftColor: 'transparent',
                borderTopLeftRadius: 0,
              },
            }}
          />
        </Box>
      </Box>

      <Box>
        {watchedJobStartSameAsOrderStart ? (
          <Tooltip title={t('order.form_v2.add_truck_time_tooltip')}>
            <span>
              <PseudoLink
                action={() => form.setValue('jobStartSameAsOrderStart', false)}
                dataUi="page-sales-orders__order-form-v2__date-and-time__add-truck-time-button"
                sx={{ mt: '26px', pl: 1 }}
                title={t('order.form_v2.add_truck_time')}
              />
            </span>
          </Tooltip>
        ) : (
          <Tooltip title={t('order.form_v2.clear_truck_time')}>
            <IconButton
              size="small"
              onClick={() => form.setValue('jobStartSameAsOrderStart', true)}
              sx={{ mt: '22px' }}
            >
              <HighlightOffOutlined sx={{ color: 'rgba(0, 0, 0, 0.87)' }} />
            </IconButton>
          </Tooltip>
        )}

        <CheckBoxFormField
          control={form.control}
          errors={form.formState.errors}
          name="jobStartSameAsOrderStart"
          value={true}
          sx={{ display: 'none' }}
        />
      </Box>
    </Box>
  );
}
